import React, { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { useHistory } from "react-router-dom";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import ErrorIcon from "@mui/icons-material/Error";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MSwitch from "../../common/switch";
import {
  disableAutomation,
  enableAutomation,
  runningAutomation,
  getAutomationInfo,
} from "../../service/automation-service";
import { useToast } from "../../common/toast";
import { useWebSocket } from "../../ws/websocket";
import { useComponentBackgroundColor } from "../../common/adaptive-theme-provider";
import AutomationWidget from "../../common/automation-widget";
import { Typography, IconButton } from "@mhome/ui";

const AutomationTitleDetail1Widget = ({ widget, onStateUpdate }) => {
  const getComponentBgColor = useComponentBackgroundColor();
  const { sendAndWaitWs } = useWebSocket();
  const { successToast, errorToast } = useToast();
  const history = useHistory();

  const [title, setTitle] = useState(widget.title);
  const [subtitle, setSubtitle] = useState(widget.subtitle);

  const [automationType, setAutomationType] = useState("");
  const [automationId, setAutomationId] = useState("");
  const [running, setRunning] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [valid, setValid] = useState(true);
  const [widgets, setWidgets] = useState([]);

  useEffect(() => {
    setTitle(widget.title);
    setSubtitle(widget.subtitle);
    setAutomationType(widget.automationType);
    setAutomationId(widget.automationId);
    setRunning(widget.running);
    setEnabled(widget.enabled);
    setValid(widget.valid);
  }, [widget]);

  // Fetch automation info including widgets
  useEffect(() => {
    const fetchAutomationInfo = async () => {
      if (!automationId || !sendAndWaitWs) {
        return;
      }

      try {
        const info = await getAutomationInfo(sendAndWaitWs, automationId);
        if (info && !info.error) {
          // Set widgets if available
          if (info.widgets && Array.isArray(info.widgets)) {
            setWidgets(info.widgets);
          } else {
            setWidgets([]);
          }
        } else {
          setWidgets([]);
        }
      } catch (error) {
        console.error("Error fetching automation info:", error);
        setWidgets([]);
      }
    };

    fetchAutomationInfo();
  }, [automationId, sendAndWaitWs]);

  const handleEnableDisable = async (event, enableDisable) => {
    if (enableDisable) {
      const response = await enableAutomation(sendAndWaitWs, automationId);
      if (response && !response.error) {
        successToast("Enabled");
        setEnabled(response.enabled);
        onStateUpdate &&
          onStateUpdate({
            ...widget,
            enabled: response.enabled,
          });
      } else {
        errorToast("Error!");
      }
    } else {
      const response = await disableAutomation(sendAndWaitWs, automationId);
      if (response && !response.error) {
        successToast("Disabled");
        setEnabled(response.enabled);
        onStateUpdate &&
          onStateUpdate({
            ...widget,
            enabled: response.enabled,
          });
      } else {
        errorToast("Error!");
      }
    }
  };

  const handleStartTaskFlow = async () => {
    const success = await runningAutomation(sendAndWaitWs, automationId);
    if (success) {
      successToast("Started");
    } else {
      errorToast("Error!");
    }
  };

  const handleOpenAutomation = (e) => {
    e.stopPropagation();
    history.push(`/automation/overview?id=${automationId}`);
  };

  return (
    <div
      className="w-full flex flex-col gap-0"
      style={{
        border: `1px solid hsl(var(--border))`,
        borderRadius: "16px",
        padding: "16px",
        backgroundColor: getComponentBgColor("hsl(var(--card))"),
        boxSizing: "border-box",
        position: "relative",
        isolation: "isolate",
      }}
    >
      {/* Title Card */}
      <div
        className="w-full flex items-center justify-between p-3 rounded-2xl"
        style={{
          backgroundColor: getComponentBgColor("hsl(var(--card))"),
        }}
      >
        <div className="flex-1 flex flex-col items-start min-w-0">
          <Typography
            className="overflow-hidden line-clamp-2 font-semibold w-full text-lg"
            style={{
              lineHeight: 1.2,
              marginBottom: subtitle ? "4px" : 0,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              className="overflow-hidden line-clamp-1 w-full text-sm opacity-70"
              style={{ color: "inherit" }}
            >
              {subtitle}
            </Typography>
          )}
        </div>

        <div className="flex items-center justify-center ml-4 shrink-0 gap-2">
          <IconButton
            onClick={handleOpenAutomation}
            size="small"
            style={{ color: "hsl(var(--primary))" }}
          >
            <OpenInNewIcon style={{ fontSize: "1.25rem" }} />
          </IconButton>
          {!valid ? (
            <ErrorIcon style={{ color: "red" }} />
          ) : automationType === "RECURRING" ? (
            <MSwitch
              checked={enabled}
              onChange={handleEnableDisable}
              inputProps={{ "aria-label": "controlled" }}
              size="medium"
            />
          ) : running ? (
            <CircularProgress size={24} />
          ) : (
            <IconButton
              onClick={(e) => {
                handleStartTaskFlow();
                e.stopPropagation();
              }}
              className="disabled:opacity-50"
              style={{ color: "hsl(var(--primary))" }}
            >
              <PlayCircleFilledWhiteIcon style={{ fontSize: "1.5rem" }} />
            </IconButton>
          )}
        </div>
      </div>

      {/* Automation Widgets */}
      {widgets && widgets.length > 0 && <AutomationWidget widgets={widgets} />}
    </div>
  );
};

export default AutomationTitleDetail1Widget;
